<template>
  <div
    v-if="inline"
    class="model-edit-form-wrapper relative flex w-full min-w-0 flex-col gap-y-2"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div class="flex min-h-0 grow flex-col space-y-2">
      <div class="min-w-0">
        <component :is="component" />
      </div>
    </div>
    <div class="grow-0 pt-2">
      <PButton
        type="button"
        :disabled="disabled || isFormInvalid"
        :loading="isSaving"
        @click="onSaveButtonClick"
        >{{ isCreating ? "Opslaan" : "Wijzigingen opslaan" }}</PButton
      >
    </div>
  </div>
  <PModal
    v-else
    :open="true"
    :title="formTitle"
    :dismissible="!disabled"
    :overlay="!disabled"
    :transition="false"
    :content="{ onInteractOutside: (event) => event.preventDefault() }"
    :ui="{
      overlay: 'z-40 bg-transparent backdrop-brightness-50',
      content:
        'z-40 h-dvh max-h-dvh w-full max-w-none overflow-visible rounded-none ring-0 sm:h-auto sm:max-w-md sm:rounded-md md:max-w-xl'
    }"
    @update:open="!$event && close()"
  >
    <template #content>
      <PForm
        :id="modalId"
        data-datepicker-portal
        :disabled="disabled || isSaving"
        :transform="false"
        @submit="onSaveButtonClick"
        class="model-edit-form-wrapper surface relative flex min-h-0 w-full flex-1 flex-col justify-between rounded-[inherit] p-2"
        :class="{ 'pointer-events-none opacity-50': disabled }"
      >
        <!-- Content -->
        <div class="flex min-h-0 grow flex-col space-y-2">
          <!-- Title -->
          <div class="flex items-center justify-between px-1">
            <span class="text-xl select-none">{{ formTitle }}</span>
            <PButton
              title="Sluiten"
              color="surface"
              size="lg"
              variant="ghost"
              @click="close"
              :icon="['fas', 'xmark']"
              class="absolute right-0 text-inherit! dark:text-inherit!"
            />
          </div>
          <div class="min-h-0 flex-1 overflow-y-auto px-1 sm:max-h-[75dvh]">
            <component :is="component" />
          </div>
        </div>

        <!-- Footer -->
        <div class="grow-0 pt-2">
          <PButton type="submit" :disabled="disabled || isFormInvalid" :loading="isSaving">{{
            isCreating ? "Opslaan" : "Wijzigingen opslaan"
          }}</PButton>
        </div>
      </PForm>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import PButton from "@/components/elements/PButton.vue"
import PForm from "@/components/form/PForm.vue"
import PModal from "@/components/overlay/PModal.vue"
import type { ModelFormConfig } from "@/interfaces/component/model-form-config.interface"
import { type Component, computed, provide, type Ref, ref, shallowRef, useId } from "vue"
import { clone, isEqual, upperFirst } from "lodash-es"
import { ModelController } from "@/controllers/model-controllers/abstract/model-controller"
import ProjectGroupModelEditForm from "@/components/project_group/ProjectGroupModelEditForm.vue"
import ModelEditFormProject from "@/components/model_edit_form/ModelEditFormProject.vue"
import ModelEditFormPerson from "@/components/model_edit_form/ModelEditFormPerson.vue"
import ModelEditFormMachine from "@/components/model_edit_form/ModelEditFormMachine.vue"
import ModelEditFormKnownBusinessEntity from "@/components/model_edit_form/ModelEditFormKnownBusinessEntity.vue"
import ModelEditFormLocation from "@/components/model_edit_form/ModelEditFormLocation.vue"
import { BaseModel } from "@/models/base.model"
import ModelEditFormPurchaseItem from "@/components/model_edit_form/ModelEditFormPurchaseItem.vue"
import ModelEditFormSaleItem from "@/components/model_edit_form/ModelEditFormSaleItem.vue"
import ModelEditFormRentalIntakeItem from "@/components/model_edit_form/ModelEditFormRentalIntakeItem.vue"
import ModelEditFormRentalDispenseItem from "@/components/model_edit_form/ModelEditFormRentalDispenseItem.vue"

const props = defineProps<{
  modelFormConfig: ModelFormConfig<BaseModel>
  disabled?: boolean
  inline?: boolean
}>()
const emit = defineEmits(["close"])
const modalId = `model-form-${useId()}`

provide("isInlineModelForm", props.inline ?? false)

// Clone the model for editing
const model = ref(clone(props.modelFormConfig.model))
// Relations are edited independently. Keep their references while snapshotting this form's fields.
const initialModel = shallowRef(clone(model.value))
const isDirty = computed(() => !isEqual(model.value, initialModel.value))

provide("model", model)
provide("initialText", props.modelFormConfig.textInput)

const controller: Ref<ModelController<any, any, any> | undefined> = ref() // Most use cases
const createHandler: Ref<(() => Promise<void>) | undefined> = ref() // Optional for custom handling
const updateHandler: Ref<(() => Promise<void>) | undefined> = ref() // Optional for custom handling
const customSaveHandler: Ref<((save: () => Promise<void>) => Promise<void>) | undefined> = ref() // Optional for custom handling
provide("controller", controller)
provide("createHandler", createHandler)
provide("updateHandler", updateHandler)
provide("customSaveHandler", customSaveHandler)
provide("closeForm", close)
provide("saveHandle", props.modelFormConfig.saveHandle)
provide("closeHandle", props.modelFormConfig.closeHandle)
provide("creationParameters", props.modelFormConfig.creationParameters)

const isCreating = computed(() => !model.value?.isSaved)
provide("isCreating", isCreating)

const autoClose = ref(true)
provide("autoClose", autoClose)

const isSaving = ref(false)

const component = computed(() => {
  const config = props.modelFormConfig
  // If a custom component is provided, use that
  if (config.component) {
    return config.component
  }

  // Otherwise, use the default component for the custom of inferred model type
  const map: { [key: string]: Component } = {
    Project: ModelEditFormProject,
    ProjectGroup: ProjectGroupModelEditForm,
    Person: ModelEditFormPerson,
    Machine: ModelEditFormMachine,
    KnownBusinessEntity: ModelEditFormKnownBusinessEntity,
    Location: ModelEditFormLocation,
    PurchaseItem: ModelEditFormPurchaseItem,
    SaleItem: ModelEditFormSaleItem,
    RentalIntakeItem: ModelEditFormRentalIntakeItem,
    RentalDispenseItem: ModelEditFormRentalDispenseItem
  }
  return map[config.modelType?.className ?? config.model.getClassName()]
})

async function onSaveButtonClick() {
  if (props.disabled || isSaving.value || isFormInvalid.value) {
    return false
  }
  isSaving.value = true
  let saved = false
  try {
    if (customSaveHandler.value) {
      await customSaveHandler.value(save) // Pass save so the custom handler can optionally call it.
    } else {
      await save()
    }

    // Write the saved values back onto the original model instance directly, not
    // via an emit: a custom save handler (e.g. the person form's updateAndClose)
    // may already have closed the form, and an emit from an unmounted component
    // is dropped, silently losing the write-back.
    if (model.value) {
      // eslint-disable-next-line vue/no-mutating-props -- writing the saved values back into the caller's model is this form's contract
      Object.assign(props.modelFormConfig.model, model.value)
    }

    if (props.modelFormConfig.saveHandle) {
      // Be sure to return the original model, because returning the copy will lead to reference issues.
      await props.modelFormConfig.saveHandle(props.modelFormConfig.model, isCreating.value)
    }
    initialModel.value = clone(model.value)
    saved = true
  } catch (error) {
    console.error(error)
    throw error
  } finally {
    if (autoClose.value && (!props.inline || saved)) {
      close()
    }
    isSaving.value = false
  }
  return saved
}

defineExpose({
  modelId: computed(() => props.modelFormConfig.model.id),
  isDirty,
  isSaving,
  saveForm: onSaveButtonClick
})

async function save() {
  if (isCreating.value) {
    await performCreate()
  } else {
    // Important to write back to model, because the backend might have changed some values (e.g. updatedAt, coords)
    model.value = await performUpdate()
  }
}

async function performCreate() {
  if (createHandler.value) {
    return await createHandler.value()
  } else if (controller.value) {
    return await controller.value.create(model.value)
  }
  throw new Error("Cannot create model without controller or createHandler")
}
provide("performCreate", performCreate)

async function performUpdate() {
  if (updateHandler.value) {
    return await updateHandler.value()
  } else if (controller.value) {
    return await controller.value.update(model.value)
  }
  throw new Error("Cannot update model without controller or updateHandler")
}

function close() {
  if (props.disabled) return
  if (props.modelFormConfig.closeHandle) {
    props.modelFormConfig.closeHandle()
  }
  emit("close")
}

const formTitle = computed(() => {
  const modelName = upperFirst(props.modelFormConfig.model.getVerboseName())
  return `${modelName} ${isCreating.value ? "toevoegen" : "wijzigen"}`
})

const isFormInvalid = ref(false)
provide("isFormInvalid", isFormInvalid)
</script>

<style scoped></style>
